import { CarrierType, DangerousGoodsDefinitionDto, OrderResponseDto } from '@planning/app/api'
import { IContainerDamage } from '@planning/components'
import { IInspectContainerFormData } from '@planning/pages/GateClerk/Components/InspectContainer'
import { formatDate, formatDateTime, formatTime } from '@planning/utils'
import { EirData } from '@tom-ui/ui'

export interface EirDataInput {
  order: Partial<OrderResponseDto> | Partial<IInspectContainerFormData>
  linkedOrder?: {
    shippingLine?: string | null
    carrierName?: string | null
  }
  visit?: {
    now?: Date
    licensePlate?: string
    driverName?: string
    driverID?: string
    companyName?: string
    customerName?: string
  }
  voyageNumber?: string | null
  consigneeAddress?: string | null
  dangerousGoodsDefinition?: DangerousGoodsDefinitionDto[] | null
  isDamaged?: boolean
  damages?: IContainerDamage[] | null
}

export function mapToEirData(input: EirDataInput): EirData {
  const now = input.visit?.now ?? new Date()
  const date = formatDate(now)!
  const time = formatTime(now)!
  const dateTime = formatDateTime(now)!

  const gross = input.order.grossWeight
  const tare = input.order.containerTare
  const net = gross && tare ? gross - tare : undefined

  const imoFromOrder = input.order.imoClasses?.length
    ? input.order.imoClasses.join(', ')
    : undefined

  const fallbackImo =
    input.dangerousGoodsDefinition
      ?.map(d => d.primaryImoClass)
      .filter(Boolean)
      .join(', ') || undefined

  const fallbackUn =
    input.dangerousGoodsDefinition
      ?.map(d => d.unNumber)
      .filter(Boolean)
      .join(', ') || undefined

  return {
    orderId: input.order.id!,
    direction: input.order.direction!,
    licensePlateNumber: input.visit?.licensePlate ?? '-',
    driver: input.visit?.driverName ?? '-',
    passport: input.visit?.driverID ?? '-',
    voyageNumber: input.voyageNumber ?? '-',
    orderNumber: input.order.referenceNumber ?? '-',
    dateOfIssue: dateTime,
    shippingLine: input.linkedOrder?.shippingLine ?? '-',
    carrierVisitName: input.linkedOrder?.carrierName ?? '-',
    imoClass: imoFromOrder ?? fallbackImo,
    unNumber: fallbackUn,
    notes: input.order.notes ?? '-',
    company: input.visit?.customerName ?? input.order.customerName ?? undefined,
    gateInDate: date,
    gateInTime: time,
    consignee: input.order.consignee ?? '-',
    consigneeAddress: input.consigneeAddress ?? undefined,
    truckCompany: input.visit?.companyName ?? '-',
    isEmpty: input.order.isEmpty,
    isDamaged: input.isDamaged,
    containerIsoCode: input.order.containerIsoCode ?? undefined,
    containerNumber: input.order.containerNumber ?? '-',
    containerType: input.order.isoCodeArchetype ?? '-',
    containerSize: input.order.containerLength?.toString() ?? '-',
    containerContent: input.order.content ?? '-',
    containerGrossWeight: gross?.toString() ?? '-',
    containerTareWeight: tare?.toString() ?? '-',
    containerNetWeight: net?.toString() ?? '-',
    containerSeals: input.order.seals?.map(seal => seal.number).join(', ') ?? '-',
  }
}

export function mapToInspectContainerFormData(
  order: OrderResponseDto,
  fallbackCarrierType: CarrierType = CarrierType.Truck,
): IInspectContainerFormData {
  return {
    ...order,
    carrierType: order.carrierVisitType ?? fallbackCarrierType,
    damages: [],
    isEdited: false,
    damagesReported: [],
    hasDamage: false,
    oog: undefined,
  }
}
